import {
  Box,
  Button,
  Chevron,
  type Colors,
  Dimensions,
  type FontsWeights,
  Icon,
  Popover,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  TimeTrackingClockInDayRowWorkedBreaksFragment,
  TimesheetPageEmployeesSettingFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import EditOvertimeRequestModal from 'modules/attendance/components/ClockIn/Modals/OvertimeRequest/Edit'
import NewOvertimeRequestModal from 'modules/attendance/components/ClockIn/Modals/OvertimeRequest/New'
import type Store from 'modules/attendance/components/ClockIn/Store'
import TooltipHeader from 'modules/attendance/components/ClockIn/Tooltip/Header'
import TooltipRow from 'modules/attendance/components/ClockIn/Tooltip/Row'
import MinutesToText from 'modules/attendance/components/MinutesToText'
import { useOvertimeRequests } from 'modules/attendance/lib/overtimeRequestsContext'
import AttendanceOvertimeUtils from 'modules/attendance/lib/utils/OvertimeRequest'

import overtimeRequestDisclaimer from './overtimeRequestDisclaimer'

interface StylesProp {
  boxGap: Dimensions
  minutes: {
    weight: FontsWeights
    color: Colors
  }
}

export interface Props {
  store: Store
  worked: number
  date: moment.Moment
  trackedMinutes: number
  workedMinutesNotApproved: number
  breaksMinutesRounded: number
  hasCorrectiveBreaks: boolean
  grayedOutColor: Colors
  hasUnresolvedShift?: boolean
  workedBreaks: TimeTrackingClockInDayRowWorkedBreaksFragment[]
  attendanceSetting?: TimesheetPageEmployeesSettingFragment
  hasTimeTrackingInconsistencies: boolean
}

export const WorkedHours = ({
  store,
  date,
  worked,
  trackedMinutes,
  workedMinutesNotApproved,
  breaksMinutesRounded,
  hasCorrectiveBreaks: hasCorrectiveBreaksFeature,
  grayedOutColor,
  workedBreaks,
  hasUnresolvedShift,
  attendanceSetting,
  hasTimeTrackingInconsistencies,
}: Props) => {
  const overtimeRequests = useOvertimeRequests()
  const [open, toggle] = useToggle(false)
  const [seeOvertimeRequest, toggleOvertimeRequest] = useToggle(false)
  const [showOvertimeModal, toggleNewOvertimeModal] = useToggle(false)
  const backendDate = dateToString(date, 'backend')
  const tracked = trackedMinutes
  const formatedDate = date.format('YYYY-MM-DD')
  const requestedOvertimeMinutes = AttendanceOvertimeUtils.requestedOvertimeMinutes(
    overtimeRequests,
    formatedDate
  )
  const overtimeRequest = overtimeRequests?.find((req) => req.date === backendDate)
  const hoursAmount = workedMinutesNotApproved / 60
  const showRequest = workedMinutesNotApproved > 0 || overtimeRequest
  const hasPaidBreaksFeature = hasFeature('time_tracking_paid_breaks')
  const hasOvertimeRequestsEnabled = attendanceSetting?.overtimeAuthorization ?? false
  const hasBreaksCorrection = hasCorrectiveBreaksFeature && breaksMinutesRounded > 0
  const showOvertimePopup = showRequest && hasOvertimeRequestsEnabled
  const showBreaksCorrectionPopup = !showOvertimePopup && hasBreaksCorrection

  const { message, button } = overtimeRequestDisclaimer({
    workedMinutesNotApproved,
    overtimeRequest,
  })

  const closeOvertimeRequestModal = () => {
    overtimeRequest ? toggleOvertimeRequest(false)() : toggleNewOvertimeModal(false)()
  }

  const seeRequest = () => {
    toggle(false)()

    overtimeRequest ? toggleOvertimeRequest(true)() : toggleNewOvertimeModal(true)()
  }

  const styles: StylesProp = {
    boxGap: 's4',
    minutes: {
      weight: 'medium',
      color:
        hasUnresolvedShift && !hasTimeTrackingInconsistencies ? 'radicalRed1200' : grayedOutColor,
    },
  }

  const { color, weight } = styles.minutes

  useEffect(() => {
    if (open) {
      track(trackingEvents.timeTracking.seeWorkedHoursTooltip, {
        user_checking_own_timesheet: store.isSeeingOwnPeriod(),
        date: backendDate,
      })
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderStatusIcon = () => {
    if (showOvertimePopup || showBreaksCorrectionPopup) return null
    return <Icon.Medium icon="timer" color={styles.minutes.color} />
  }

  const renderModals = () => (
    <>
      {seeOvertimeRequest && overtimeRequest && (
        <EditOvertimeRequestModal
          overtimeRequest={overtimeRequest}
          onClose={closeOvertimeRequestModal}
        />
      )}
      {showOvertimeModal && (
        <NewOvertimeRequestModal
          date={formatedDate}
          employeeId={store.employeeId}
          hoursAmount={hoursAmount}
          onClose={closeOvertimeRequestModal}
        />
      )}
    </>
  )

  const renderPopoverTrigger = () => (
    <Box inline flexDirection="row" gap={styles.boxGap} alignItems="center">
      {showOvertimePopup && (
        <Box paddingRight="s4">
          <Icon.Medium icon="alert-circle" color="champagne1200" />
        </Box>
      )}
      {showBreaksCorrectionPopup && (
        <Box paddingRight="s4">
          <Icon.Medium icon="info-circle" color="viridian1000" />
        </Box>
      )}
      <MinutesToText value={worked} weight={weight} color={color} alwaysRemainder />
      <Chevron direction={open ? 'up' : 'down'} type="terciary" />
    </Box>
  )

  const renderTooltipRows = () => (
    <>
      <TooltipHeader title={i18n.t('clock_in.worked_hours_title')} minutes={worked} />
      <TooltipRow label={i18n.t('clock_in.effective_worked_time')} value={worked} multiLine />
      <TooltipRow label={i18n.t('clock_in.time_tracked')} value={tracked} multiLine />

      {hasPaidBreaksFeature &&
        workedBreaks.map((workedBreak) => {
          const breakName = workedBreak.timeSettingsBreakConfiguration?.name ?? ''
          return (
            <TooltipRow
              key={`${workedBreak.date}-${workedBreak.employeeId}-${breakName}`}
              label={breakName}
              value={workedBreak.trackedMinutes}
              multiLine
            />
          )
        })}

      {hasBreaksCorrection && (
        <TooltipRow
          label={i18n.t('clock_in.breaks.corrective_breaks.title')}
          value={-breaksMinutesRounded}
          multiLine
        />
      )}

      {hasOvertimeRequestsEnabled && (
        <TooltipRow
          label={i18n.t('clock_in.approved_overtime')}
          value={requestedOvertimeMinutes}
          multiLine
        />
      )}
    </>
  )

  const renderOvertimeDescription = () => {
    if (!showOvertimePopup) return null

    return (
      <Box inline gap="s16" textAlign="left" alignItems="center" paddingY="s4">
        <Text color="grey000White" size="200">
          {message}
        </Text>
        <Button type="secondary" onClick={seeRequest}>
          {button}
        </Button>
      </Box>
    )
  }

  const renderBreaksCorrectionDescription = () => {
    if (!showBreaksCorrectionPopup) return null

    return (
      <Box inline gap="s16" textAlign="left" alignItems="center" paddingTop="s16">
        <Text color="grey000White" size="200">
          {i18n.t('clock_in.breaks.corrective_breaks.description')}
        </Text>
      </Box>
    )
  }

  return (
    <>
      {renderStatusIcon()}
      {renderModals()}

      <Popover.ItemList attachment="bottom center" toggle={toggle} open={open} gapDivider={false}>
        {renderPopoverTrigger()}
        {renderTooltipRows()}
        {renderOvertimeDescription()}
        {renderBreaksCorrectionDescription()}
      </Popover.ItemList>
    </>
  )
}
